// src/components/Header/Header.tsx
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { 
  User, 
  Settings, 
  BookOpen, 
  Heart, 
  LogOut, 
  Menu, 
  X, 
  ChevronDown,
  Film,
  FileText,
  LayoutDashboard,
  Clapperboard,
  Users,
  Megaphone,
  Handshake,
  Sparkles,
  Headphones,
  Wand2,
  Eye,
  IdCard
} from 'lucide-react';
import type { RootState } from '../../store';
import ProfileSwitcher from '../Profiles/ProfileSwitcher';
import { userActions } from '../../store/reducers/userReducers';
import { useAppDispatch } from '../../store';
import { performCompleteLogout } from '../../utils/authUtils';



const Header = () => {
  const dispatch = useAppDispatch(); // Use typed dispatch  
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  // One key at a time: 'studio' | 'watch' | 'community' | null
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  
  const { userInfo } = useSelector((state: RootState) => state.user) as {
    
    userInfo: {
      avatar?: string;
      avatarUrl?: string;
      firstName?: string;
      lastName?: string;
      email?: string;
      isAdmin?: boolean;
      admin?: boolean;  // Support both property names
      // add other properties as needed
    } | null;
  };

  // Debug log to check user data
  
  // Helper function to check if user is admin (support both property names)
  const isUserAdmin = (user: any) => {
    return user?.isAdmin || user?.admin;
  };



  
  // Helper function to clean up URLs with double slashes
  const cleanUrl = (url: string | undefined) => {
    if (!url) return url;
    // Replace multiple slashes with single slash, but preserve protocol slashes
    return url.replace(/([^:]\/)\/+/g, '$1');
  };
  
  const avatarUrl = cleanUrl(userInfo?.avatar || userInfo?.avatarUrl);

  


  const isAuthenticated = !!userInfo;
  const navigate = useNavigate();

  // Primary navigation. StrymHub is a studio first and a streaming platform second,
  // so Studio and Community sit alongside Watch rather than under it.
  // Order matters and lives here, in one place.
  //
  // Watch comes first deliberately: StrymHub is a company rather than only a streaming
  // site, but the thing a visitor can do immediately is watch what it has made, and
  // three dropdowns ahead of it buried that. Live follows because it is time-sensitive -
  // if something is on now, it is the most perishable thing on the page.
  //
  // Live is an entry in this array rather than markup after it. It used to be hardcoded
  // after the loop in the desktop nav AND again in the mobile menu, which is why
  // changing the order was not a one-line change and why the two could disagree.
  const navMenus: Array<
    | { kind: 'link'; key: string; label: string; path: string }
    | { kind: 'group'; key: string; label: string; items: Array<{ name: string; path: string; icon: React.ElementType; description?: string }> }
  > = [
    {
      kind: 'group' as const,
      key: 'watch',
      label: 'Watch',
      items: [
        { name: 'Originals', path: '/originals', icon: Sparkles, description: 'Made by StrymHub' },
        { name: 'Cinema', path: '/media/cinema', icon: Film, description: 'Films and shorts' },
        { name: 'Contents', path: '/media/contents', icon: FileText, description: 'Everything else' },
        { name: 'Podcasts', path: '/podcasts', icon: Headphones, description: 'Long-form conversations' },
        { name: 'Animations', path: '/animations', icon: Wand2, description: 'Original animated work' },
        { name: 'Sneak Peeks', path: '/sneak-peeks', icon: Eye, description: 'Trailers and works in progress' }
      ]
    },
    {
      kind: 'link' as const,
      key: 'live',
      label: 'Live',
      path: '/live',
    },
    {
      kind: 'group' as const,
      key: 'studio',
      label: 'Studio',
      items: [
        { name: 'StrymHub Studios', path: '/studios', icon: Clapperboard, description: 'How we make our work' },
        { name: 'Productions', path: '/productions', icon: Film, description: 'Everything on the slate' },
        { name: 'Partner with us', path: '/partners', icon: Handshake, description: 'Sponsored & co-produced work' }
      ]
    },
    {
      kind: 'group' as const,
      key: 'community',
      label: 'Community',
      items: [
        { name: 'The Community', path: '/community', icon: Users, description: 'What membership means' },
        { name: 'The Troupe', path: '/community/troupe', icon: IdCard, description: 'Every member, every craft' },
        { name: 'Open Calls', path: '/casting', icon: Megaphone, description: 'Casting and crew positions' },
        { name: 'Join the Troupe', path: '/community/join', icon: Sparkles, description: 'Get your own page' }
      ]
    }
  ];

  const profileMenuItems = [
    { icon: User, label: 'Profile', path: '/profile' },
    // "Who's watching" rather than "Profiles": two entries above are already called
    // Profile and Member Profile, and a third would be indistinguishable. This one is
    // about which viewer on the account is watching, which is a different question
    // from either of those.
    { icon: Users, label: "Who's watching", path: '/profiles' },
    { icon: IdCard, label: 'Member Profile', path: '/community/join' },
    { icon: Megaphone, label: 'My Applications', path: '/casting/my-applications' },
    { icon: BookOpen, label: 'My Library', path: '/library' },
    { icon: Heart, label: 'Favorites', path: '/favorites' },
    { icon: Settings, label: 'Settings', path: '/settings' }
  ];

  const adminMenuItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/admin' }
  ];

  const handleSignOut = () => {
    performCompleteLogout().then(() => {
      dispatch(userActions.resetUserInfo());
      navigate('/login');
    }).catch(() => {
      // Silently handle logout failure
    });
  }

  const handleMenuClick = () => {
    setIsProfileDropdownOpen(false);
    setOpenMenu(null);
  };

  return (
    <nav className="bg-gradient-to-r from-slate-950 via-neutral-900 to-slate-950 backdrop-blur-md border-b border-orange-500/10 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          
          {/* Logo */}
          <div className="flex-shrink-0">
            <Link to="/" className="logo-text text-2xl font-display bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 bg-clip-text text-transparent text-glow-hover transition-all duration-300 hover:drop-shadow-[0_0_15px_rgba(255,69,0,0.5)]">
              strymhub
            </Link>
          </div>

          {/* Desktop Navigation - Changed to lg: for better tablet support */}
          <div className="hidden lg:block">
            <div className="ml-10 flex items-baseline space-x-6" data-testid="desktop-nav">
              {navMenus.map((menu) => menu.kind === 'link' ? (
                <Link
                  key={menu.key}
                  to={menu.path}
                  className="text-gray-300 hover:text-orange-400 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 hover:bg-orange-500/10 flex items-center gap-1.5"
                >
                  {/* The dot is the point: it says something is on now, which a word
                      cannot. Decorative, so it is hidden from assistive tech - the link
                      text already says Live. */}
                  <span className="relative flex h-2 w-2" aria-hidden="true">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                  </span>
                  {menu.label}
                </Link>
              ) : (
                <div key={menu.key} className="relative">
                  <button
                    onClick={() => setOpenMenu(openMenu === menu.key ? null : menu.key)}
                    aria-expanded={openMenu === menu.key}
                    aria-haspopup="true"
                    className="text-gray-300 hover:text-orange-400 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 hover:bg-orange-500/10 flex items-center"
                  >
                    {menu.label}
                    <ChevronDown
                      className={`h-4 w-4 ml-1 transition-transform duration-200 ${openMenu === menu.key ? 'rotate-180' : ''}`}
                      aria-hidden="true"
                    />
                  </button>

                  {openMenu === menu.key && (
                    <div className="absolute left-0 mt-2 w-72 bg-slate-900/95 backdrop-blur-md rounded-xl shadow-2xl border border-white/10 py-2 z-50">
                      {menu.items.map((item) => (
                        <Link
                          key={item.path}
                          to={item.path}
                          onClick={handleMenuClick}
                          className="flex items-start gap-3 w-full px-4 py-2.5 text-sm text-gray-300 hover:text-orange-300 hover:bg-orange-500/10 transition-colors duration-150"
                        >
                          <item.icon className="h-4 w-4 mt-0.5 shrink-0 text-orange-400/80" aria-hidden="true" />
                          <span>
                            <span className="block font-medium">{item.name}</span>
                            <span className="block text-xs text-gray-500">{item.description}</span>
                          </span>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}

            </div>
          </div>

          {/* Auth Section - Changed to lg: for better tablet support */}
          <div className="hidden lg:block">
            <div className="ml-4 flex items-center gap-2 lg:ml-6">
              {/* Only when signed in: profiles belong to an account, and there is
                  nothing to switch between without one. */}
              {isAuthenticated && <ProfileSwitcher />}
              {!isAuthenticated ? (
                <Link
                  to="/login"
                  className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white px-6 py-2 rounded-full font-semibold transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-orange-500/30 animate-fire-glow"
                >
                  Sign In
                </Link>
              ) : (
                <div className="flex items-center space-x-3">
                  <div className="relative">
                  <button
                    onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                    className="flex items-center space-x-2 text-gray-300 hover:text-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 focus:ring-offset-gray-800 rounded-full p-1 transition-all duration-200"
                  >
                    <div className="h-8 w-8 rounded-full bg-gradient-to-r from-orange-500 to-red-500 flex items-center justify-center">
                      {avatarUrl ? (
                        <img 
                          src={avatarUrl} 
                          alt="Profile" 
                          className="h-8 w-8 rounded-full object-cover"
                          onError={(e) => {
                            const img = e.target as HTMLImageElement;
                            // Try Backblaze S3 URL as fallback if CDN fails
                            // Handles both prod (strymhubprod) and dev (strymhubdev) buckets
                            if (avatarUrl.includes('.b-cdn.net') && !img.dataset.triedFallback) {
                              img.dataset.triedFallback = 'true';
                              // Extract bucket name from CDN URL (e.g., strymhubprod from strymhubprod.b-cdn.net)
                              const bucketMatch = avatarUrl.match(/https:\/\/([^.]+)\.b-cdn\.net\//);
                              if (bucketMatch) {
                                const bucketName = bucketMatch[1];
                                const filePath = avatarUrl.replace(`https://${bucketName}.b-cdn.net/`, '');
                                img.src = `https://${bucketName}.s3.eu-central-003.backblazeb2.com/${filePath}`;
                                return;
                              }
                            }
                            img.style.display = 'none';
                          }}
                        />
                      ) : (
                        <User className="h-5 w-5 text-white" />
                      )}
                    </div>
                    <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${isProfileDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {/* Profile Dropdown */}
                  {isProfileDropdownOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-slate-800/95 backdrop-blur-md rounded-md shadow-lg border border-white/10 py-1 z-50">
                      <div className="px-4 py-2 border-b border-white/10">
                        <p className="text-sm font-medium text-white truncate">{userInfo?.firstName} {userInfo?.lastName}</p>
                        <p className="text-xs text-gray-400 truncate">{userInfo?.email}</p>
                      </div>
                      
                      {isUserAdmin(userInfo) && (
                        <>
                          {adminMenuItems.map((item) => (
                            <Link
                              key={item.label}
                              to={item.path}
                              onClick={handleMenuClick}
                              className="flex items-center w-full px-4 py-2 text-sm text-gray-300 hover:text-orange-400 hover:bg-orange-500/10 transition-colors duration-200"
                            >
                              <item.icon className="h-4 w-4 mr-3" />
                              {item.label}
                            </Link>
                          ))}
                          <div className="border-b border-white/10 my-1"></div>
                        </>
                      )}
                      
                      {profileMenuItems.map((item) => (
                        <Link
                          key={item.label}
                          to={item.path}
                          onClick={handleMenuClick}
                          className="flex items-center w-full px-4 py-2 text-sm text-gray-300 hover:text-orange-400 hover:bg-orange-500/10 transition-colors duration-200"
                        >
                          <item.icon className="h-4 w-4 mr-3" />
                          {item.label}
                        </Link>
                      ))}
                      
                      <div className="border-t border-white/10 mt-1">
                        <button
                          onClick={handleSignOut}
                          className="flex items-center w-full px-4 py-2 text-sm text-gray-300 hover:text-orange-400 hover:bg-orange-500/10 transition-colors duration-200"
                        >
                          <LogOut className="h-4 w-4 mr-3" />
                          Sign Out
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                </div>
              )}
            </div>
          </div>

          {/* Mobile menu button - Changed to lg:hidden for better tablet support */}
          <div className="lg:hidden">
            <button
              type="button"
              // Named, because it had no accessible name at all - an icon-only button
              // announces as "button" and nothing else.
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMobileMenuOpen}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="text-gray-400 hover:text-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white p-2"
            >
              {isMobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu - Changed to lg:hidden for better tablet support */}
      {isMobileMenuOpen && (
        /* Bounded and scrollable, which it was not.
           The menu holds around twenty links across several groups, and the nav above it
           is sticky top-0 - so on a phone the panel ran well past the bottom of the
           screen and whatever sat last was simply unreachable. That was the Sign In
           button: a signed-out visitor on mobile could see every nav item and had no way
           to sign in. 4rem is the h-16 bar above; dvh rather than vh so the browser's own
           chrome is accounted for. */
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-slate-900/95 backdrop-blur-md border-t border-white/10 max-h-[calc(100dvh-4rem)] overflow-y-auto overscroll-contain"
        >
          {/* Auth first, deliberately. For a signed-out visitor this is the whole
              point of opening the menu, and it used to sit below every nav group -
              around twenty links down, past the bottom of a phone screen. */}
          {/* Mobile Auth Section */}
          {/* border-b, not border-t: the rule used to separate this from the nav above
              it, and now it separates it from the nav below. */}
          <div className="pt-4 pb-4 border-b border-white/10">
            {!isAuthenticated ? (
              <div className="px-5">
                <Link
                  to="/login"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-700 hover:to-red-700 text-white px-6 py-3 rounded-full font-medium transition-all duration-200 block text-center"
                >
                  Sign In
                </Link>
              </div>
            ) : (
              <>
                <div className="flex items-center px-5">
                  <div className="h-10 w-10 rounded-full bg-gradient-to-r from-orange-500 to-red-500 flex items-center justify-center">
                    {avatarUrl ? (
                      <img 
                        src={avatarUrl} 
                        alt="Profile" 
                        className="h-10 w-10 rounded-full object-cover"
                        onError={(e) => {
                          const img = e.target as HTMLImageElement;
                          // Try multiple Backblaze URL formats as fallback
                          if (avatarUrl && avatarUrl.includes('b-cdn.net') && !img.src.includes('backblazeb2.com')) {
                            // First try the file API format
                            const fileApiUrl = avatarUrl.replace('https://strymhubdev.b-cdn.net/', 'https://f003.backblazeb2.com/file/strymhubdev/');
                            img.src = fileApiUrl;
                          } else if (avatarUrl && img.src.includes('f003.backblazeb2.com') && !img.src.includes('s3.eu-central-003')) {
                            // If file API failed, try S3 compatible URL
                            const s3Url = avatarUrl.replace('https://strymhubdev.b-cdn.net/', 'https://strymhubdev.s3.eu-central-003.backblazeb2.com/');
                            img.src = s3Url;
                          } else {
                            img.style.display = 'none';
                          }
                        }}
                      />
                    ) : (
                      <User className="h-6 w-6 text-white" />
                    )}
                  </div>
                  <div className="ml-3">
                    <div className="text-base font-medium text-white truncate">{userInfo?.firstName} {userInfo?.lastName}</div>
                    <div className="text-sm font-medium text-gray-400 truncate">{userInfo?.email}</div>
                  </div>
                </div>
                
                <div className="mt-3 px-2 space-y-1">
                  {isUserAdmin(userInfo) && (
                    <>
                      {adminMenuItems.map((item) => (
                        <Link
                          key={item.label}
                          to={item.path}
                          onClick={() => setIsMobileMenuOpen(false)}
                          className="flex items-center w-full px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:text-orange-400 hover:bg-orange-500/10 transition-colors duration-200"
                        >
                          <item.icon className="h-5 w-5 mr-3" />
                          {item.label}
                        </Link>
                      ))}
                      <div className="border-b border-white/10 my-2"></div>
                    </>
                  )}
                  
                  {profileMenuItems.map((item) => (
                    <Link
                      key={item.label}
                      to={item.path}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="flex items-center w-full px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:text-orange-400 hover:bg-orange-500/10 transition-colors duration-200"
                    >
                      <item.icon className="h-5 w-5 mr-3" />
                      {item.label}
                    </Link>
                  ))}
                  
                  <button
                    onClick={() => {
                      handleSignOut();
                      setIsMobileMenuOpen(false);
                    }}
                    className="flex items-center w-full px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:text-orange-400 hover:bg-orange-500/10 transition-colors duration-200"
                  >
                    <LogOut className="h-5 w-5 mr-3" />
                    Sign Out
                  </button>
                </div>
              </>
            )}
          </div>

          <div className="px-2 pt-3 pb-4 space-y-5 sm:px-3" data-testid="mobile-nav">
            {navMenus.map((menu) => menu.kind === 'link' ? (
              <Link
                key={menu.key}
                to={menu.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className="flex items-center gap-2 text-gray-300 hover:text-orange-400 px-3 py-2 rounded-md text-base font-medium hover:bg-orange-500/10 transition-colors duration-150"
              >
                <span className="relative flex h-2 w-2" aria-hidden="true">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                </span>
                {menu.label}
              </Link>
            ) : (
              <div key={menu.key}>
                <p className="px-3 text-xs font-semibold uppercase tracking-[0.18em] text-orange-400/80">
                  {menu.label}
                </p>
                <div className="mt-2 space-y-1">
                  {menu.items.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="flex items-center gap-3 text-gray-300 hover:text-orange-400 px-3 py-2 rounded-md text-base font-medium hover:bg-orange-500/10 transition-colors duration-150"
                    >
                      <item.icon className="h-4 w-4 text-orange-400/70" aria-hidden="true" />
                      {item.name}
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
          
        </div>
      )}

      {/* Click outside handlers */}
      {(isProfileDropdownOpen || openMenu !== null) && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => {
            setIsProfileDropdownOpen(false);
            setOpenMenu(null);
          }}
        />
      )}
    </nav>
  );
};

export default Header;